import { computed, inject, Injectable, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { JobStateService } from "./job-state.store";
import { MinMax, ViewerSettingsService } from "./viewer-settings.store";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { JobDetail } from "../../../models/jobDetail";
import {
  BatchOutput,
  CapturedImage,
  CaptureGroup,
  delay,
  downloadCaptures,
  downloadDataUrl,
  nextFrame,
  withBackgroundSafeRaf,
} from "../../../shared/batch-capture";

import { FrameCaptureOptions, ScreenshotFrameService } from "../../../shared/services/screenshot-frame.service";

export type { BatchOutput } from "../../../shared/batch-capture";

export type BatchIterationMode = "last" | "first" | "first-last" | "custom";
export type BatchTarget = "full" | "inline" | "crossline" | "depth";

export interface BatchRequest {
  jobIds: string[];
  iterationMode: BatchIterationMode;
  customIterations?: number[];
  targets: BatchTarget[];
  models?: string[]; // model property ids; empty/absent -> currently selected model
  output?: BatchOutput; // defaults to zip
}

// caption shown under each capture on a slide
const SLIDE_CAPTIONS: Record<BatchTarget, string> = {
  full: "Full Grid",
  inline: "Inline",
  crossline: "Crossline",
  depth: "Depth Slice",
};

// colour limits pinned per model for the duration of a batch run, so every
// capture of the same model shares one scale and is directly comparable
interface PinnedLimits {
  inline: MinMax | null;
  depth: MinMax | null;
}

// upper bound for a single job/iteration to load before we capture anyway,
// so one stuck request can't hang the whole batch
const SETTLE_TIMEOUT_MS = 60_000;
// slice resources flip to loading asynchronously after a signal change,
// so give them a moment before we start watching for "everything idle"
const SETTLE_GRACE_MS = 500;
// resources can chain into one another (e.g. models list -> slice image),
// require a short fully-idle window before declaring the viewer settled
const SETTLE_STABLE_MS = 400;

@Injectable({ providedIn: "root" })
export class BatchScreenshotService {
  private jobStateService = inject(JobStateService);
  private viewerSettings = inject(ViewerSettingsService);
  private cachedProjectService = inject(CachedProjectService);
  private nzNotificationService = inject(NzNotificationService);
  private screenshotFrame = inject(ScreenshotFrameService);

  // --- viewer readiness ---
  // slice components report their resource loading state here so the batch
  // runner knows when the viewer has settled after a job/iteration change
  private sliceLoading = signal<Record<string, boolean>>({});
  readonly viewerBusy = computed(() => Object.values(this.sliceLoading()).some(Boolean));

  reportSliceLoading(key: string, loading: boolean) {
    this.sliceLoading.update((s) => (s[key] === loading ? s : { ...s, [key]: loading }));
  }

  // --- batch run state (drives the progress UI in the settings drawer) ---
  readonly running = signal(false);
  readonly done = signal(0);
  readonly total = signal(0);
  readonly currentLabel = signal("");
  private cancelRequested = false;

  cancel() {
    this.cancelRequested = true;
  }

  iterationsPerJob(request: BatchRequest): number {
    switch (request.iterationMode) {
      case "first-last":
        return 2;
      case "custom":
        return request.customIterations?.length ?? 0;
      default:
        return 1;
    }
  }

  shotsPerJob(request: BatchRequest): number {
    return (
      this.iterationsPerJob(request) *
      Math.max(request.models?.length ?? 0, 1) *
      new Set(request.targets).size
    );
  }

  private resolveIterations(request: BatchRequest, iterationsComplete: number): number[] {
    const last = iterationsComplete ?? 0;
    switch (request.iterationMode) {
      case "last":
        return [last];
      case "first":
        return [0];
      case "first-last":
        return [...new Set([0, last])];
      case "custom":
        return [...new Set((request.customIterations ?? []).map((i) => Math.max(0, Math.min(i, last))))];
    }
  }

  async runBatch(request: BatchRequest) {
    if (this.running()) return;
    const jobIds = [...new Set(request.jobIds)];
    const targets = [...new Set(request.targets)];
    if (jobIds.length === 0 || targets.length === 0 || this.iterationsPerJob(request) === 0) return;

    const originalJobId = this.jobStateService.jobData()?.id;
    const originalIteration = this.jobStateService.iteration();
    const originalModel = this.jobStateService.selectedModel();
    const originalSync = this.jobStateService.synchroniseSettings();
    const originalPreserve = this.jobStateService.preserveRanges();
    const originalAuto = this.viewerSettings.auto();
    const originalInlineMinMax = this.viewerSettings.inlineMinMax();
    const originalDepthMinMax = this.viewerSettings.depthMinMax();
    const originalShowDepth = this.viewerSettings.showDepthSlice();

    const models = request.models?.length ? [...new Set(request.models)] : [originalModel];
    const shotsPerIteration = models.length * targets.length;

    // keep model/colormap/positions identical across jobs so the captures are comparable
    this.jobStateService.synchroniseSettings.set(true);
    this.jobStateService.preserveRanges.set(true);
    // the depth slice only renders while toggled on — show it for the run so it can be captured
    if (targets.includes("depth")) this.viewerSettings.showDepthSlice.set(true);

    this.running.set(true);
    this.cancelRequested = false;
    this.done.set(0);
    // rough total up-front; corrected per job once its detail (iterationsComplete) is known
    this.total.set(jobIds.length * this.iterationsPerJob(request) * shotsPerIteration);

    const output = request.output ?? "zip";
    // one group per job/iteration/model — a slide each in slides mode
    const groups: CaptureGroup[] = [];
    const failures: string[] = [];
    // per-job cached settings (and auto mode) would give each job/iteration its own
    // colour scale — pin the limits from each model's first render for the whole run
    const pinnedLimits: Record<string, PinnedLimits> = {};

    try {
      for (const jobId of jobIds) {
        if (this.cancelRequested) break;

        let job: JobDetail;
        try {
          job = await firstValueFrom(this.cachedProjectService.getJobDetail(jobId));
        } catch (err) {
          console.error("Batch screenshot: failed to load job", jobId, err);
          failures.push(jobId);
          this.total.update((t) => t - this.iterationsPerJob(request) * shotsPerIteration);
          continue;
        }

        const iterations = this.resolveIterations(request, job.iterationsComplete);
        this.total.update(
          (t) => t + (iterations.length - this.iterationsPerJob(request)) * shotsPerIteration,
        );

        for (const iteration of iterations) {
          if (this.cancelRequested) break;
          this.jobStateService.iteration.set(iteration);

          for (const model of models) {
            if (this.cancelRequested) break;
            const label = `${job.name} · CP ${iteration} · ${model}`;
            this.currentLabel.set(label);
            this.jobStateService.selectedModel.set(model);

            if (pinnedLimits[model]) {
              this.applyPinnedLimits(pinnedLimits[model]);
              await this.waitForViewerSettled();
            } else {
              // first render of this model: let it settle with whatever limits it
              // resolves to, then pin the effective scale for the rest of the run
              await this.waitForViewerSettled();
              pinnedLimits[model] = this.deriveEffectiveLimits();
              if (pinnedLimits[model].inline || pinnedLimits[model].depth) {
                this.applyPinnedLimits(pinnedLimits[model]);
                // re-settles only if this actually changed the limits (e.g. auto was on)
                await this.waitForViewerSettled();
              }
            }
            if (this.cancelRequested) break;

            const group: CaptureGroup = { title: label, shots: [] };
            for (const target of targets) {
              // silently skip views that aren't on screen (e.g. crossline/depth on a 2d job)
              const elementId = target === "full" ? "ss-div" : `${target}-slice`;
              if (!document.getElementById(elementId)) {
                this.total.update((t) => t - 1);
                continue;
              }
              try {
                group.shots.push(await this.captureTarget(target));
              } catch (err) {
                console.error("Batch screenshot failed for", label, target, err);
                failures.push(`${label} (${target})`);
              }
              this.done.update((d) => d + 1);
            }
            if (group.shots.length) groups.push(group);
          }
        }
      }

      const shots = groups.flatMap((g) => g.shots);
      const outputLabel = output === "slides" ? "added to a slide deck" : "downloaded as a zip";
      if (shots.length) {
        const projectName = this.jobStateService.jobData()?.projectName ?? "project";
        const baseName = `${stripDash(projectName)}-screenshots-${new Date().toISOString().slice(0, 10)}`;
        await downloadCaptures(output, groups, baseName);
      }

      // sticky (nzDuration 0) so the outcome isn't missed after a background-tab run
      if (this.cancelRequested) {
        this.nzNotificationService.info(
          "Batch screenshots cancelled",
          `${shots.length} screenshot(s) were captured before cancelling.`,
          { nzDuration: 0 },
        );
      } else if (failures.length) {
        this.nzNotificationService.warning(
          "Batch screenshots finished with errors",
          `Captured ${shots.length} screenshot(s). Failed: ${failures.join(", ")}`,
          { nzDuration: 0 },
        );
      } else {
        this.nzNotificationService.success(
          "Batch screenshots complete",
          `${shots.length} screenshot(s) ${outputLabel}.`,
          { nzDuration: 0 },
        );
      }
    } finally {
      this.jobStateService.synchroniseSettings.set(originalSync);
      this.jobStateService.preserveRanges.set(originalPreserve);
      // put the viewer back on the job/iteration/model the user was looking at
      if (originalJobId) {
        try {
          await firstValueFrom(this.cachedProjectService.getJobDetail(originalJobId));
          this.jobStateService.iteration.set(originalIteration);
          this.jobStateService.selectedModel.set(originalModel);
        } catch (err) {
          console.error("Batch screenshot: failed to restore original job", err);
        }
      }
      // undo any limit pinning (after job/model restore, so these .sets win)
      this.viewerSettings.auto.set(originalAuto);
      this.viewerSettings.inlineMinMax.set(originalInlineMinMax);
      this.viewerSettings.depthMinMax.set(originalDepthMinMax);
      this.viewerSettings.showDepthSlice.set(originalShowDepth);
      this.currentLabel.set("");
      this.running.set(false);
    }
  }

  private applyPinnedLimits(pin: PinnedLimits) {
    // explicit limits only apply while auto-scaling is off
    this.viewerSettings.auto.set(false);
    if (pin.inline) this.viewerSettings.inlineMinMax.set(pin.inline);
    if (pin.depth) this.viewerSettings.depthMinMax.set(pin.depth);
  }

  // The scale the current render actually used: the explicit min/max if one is set,
  // otherwise the true limits the slices reported back when auto-scaling
  private deriveEffectiveLimits(): PinnedLimits {
    const auto = this.viewerSettings.auto();
    const effective = (current: MinMax, slice: "inline" | "depth"): MinMax | null => {
      if (!auto && current?.min != null && current?.max != null) return current;
      const min = this.viewerSettings.trueMin()[slice];
      const max = this.viewerSettings.trueMax()[slice];
      return min != null && max != null ? { min, max } : null;
    };
    return {
      inline: effective(this.viewerSettings.inlineMinMax(), "inline"),
      depth: effective(this.viewerSettings.depthMinMax(), "depth"),
    };
  }

  private async waitForViewerSettled() {
    await delay(SETTLE_GRACE_MS);
    const start = Date.now();
    let idleSince: number | null = null;
    while (Date.now() - start < SETTLE_TIMEOUT_MS) {
      if (this.cancelRequested) return;
      if (!this.viewerBusy()) {
        idleSince ??= Date.now();
        if (Date.now() - idleSince >= SETTLE_STABLE_MS) break;
      } else {
        idleSince = null;
      }
      await delay(100);
    }
    // two frames so the canvases repaint with the loaded data before capture
    await nextFrame();
    await nextFrame();
  }

  // Skips rendering of small UI chrome (toolbar buttons, metrics readouts) marked with
  // this class. Only safe where a blank gap in the snapshot is acceptable — large grid
  // panels are handled with display:none + grid reflow in captureFull instead, because
  // the filter only skips drawing a node, it does not remove it from layout.
  private readonly omitFilter = (node: Element) => !node.classList?.contains("omit-from-screenshot");

  // Re-framing a capture (slide / fixed width, see ScreenshotFrameService) resizes the
  // slice containers, which can make the viewers redraw — wait for any loading to finish too
  private readonly frameCaptureOptions: FrameCaptureOptions = {
    filter: this.omitFilter,
    wrapCapture: withBackgroundSafeRaf,
    waitForReady: async () => {
      const start = Date.now();
      while (this.viewerBusy() && Date.now() - start < SETTLE_TIMEOUT_MS) await delay(100);
      await nextFrame();
    },
  };

  async captureTarget(target: BatchTarget): Promise<CapturedImage> {
    if (target === "full") return this.captureFull();

    const element = document.getElementById(`${target}-slice`);
    if (!element) throw new Error(`The ${target} slice is not on screen`);
    const dataUrl = await this.screenshotFrame.capture(element, this.frameCaptureOptions);
    return { name: this.buildSliceScreenshotName(target), dataUrl, caption: SLIDE_CAPTIONS[target] };
  }

  // Captures the full viewer grid ('ss-div') exactly like the manual screenshot
  // button, but returns the image instead of downloading it.
  async captureFull(): Promise<CapturedImage> {
    const element = document.getElementById("ss-div");
    if (!element) throw new Error("Model viewer is not on screen");
    const vProfile = document.getElementById("v-profile");
    const depthSlice = document.getElementById("depth-slice");

    // Large grid panels need a real layout removal (display:none + gridTemplateAreas rewrite)
    // so the surrounding grid collapses correctly in the snapshot — the omit filter only
    // affects canvas drawing, not layout. Originals are restored in finally.
    const hideVP = !!vProfile && !this.viewerSettings.screenshotSettings.withVP();
    const hideDepth = !!depthSlice && !this.viewerSettings.screenshotSettings.withDepth();

    let originalDisplayVP: string;
    let originalGridTemplateAreas: string;
    let originalDisplayDepth: string;

    if (hideVP) {
      originalDisplayVP = vProfile.style.display;
      vProfile.style.display = "none";
      originalGridTemplateAreas = element.style.gridTemplateAreas;
      element.style.gridTemplateAreas = `"inline crossline""depth profile"`;
    }
    if (hideDepth) {
      originalDisplayDepth = depthSlice.style.display;
      depthSlice.style.display = "none";
    }

    try {
      const dataUrl = await this.screenshotFrame.capture(element, this.frameCaptureOptions);
      return { name: this.buildFullScreenshotName(), dataUrl, caption: SLIDE_CAPTIONS.full };
    } finally {
      if (hideVP) {
        vProfile.style.display = originalDisplayVP;
        element.style.gridTemplateAreas = originalGridTemplateAreas;
      }
      if (hideDepth) {
        depthSlice.style.display = originalDisplayDepth;
      }
    }
  }

  private buildSliceScreenshotName(target: Exclude<BatchTarget, "full">): string {
    const job = this.jobStateService.jobData();
    const sliceTag =
      target === "inline"
        ? `IL-${this.jobStateService.inline()}`
        : target === "crossline"
          ? `XL-${this.jobStateService.crossline()}`
          : `DEP-${this.jobStateService.depth()}`;
    return (
      `${stripDash(job.projectName.substring(0, 7))}-${stripDash(job.name.substring(0, 7))}` +
      `-${sliceTag}-CPNUM${this.jobStateService.iteration()}-${this.jobStateService.selectedModel()}` +
      `-Height${this.viewerSettings.viewerHeight()}${this.screenshotFrame.fileTag()}-${job.id}.png`
    );
  }

  private buildFullScreenshotName(): string {
    const job = this.jobStateService.jobData();
    const prefix = `${stripDash(job.projectName.substring(0, 7))}-${stripDash(job.name.substring(0, 7))}`;
    const suffix = `CPNUM${this.jobStateService.iteration()}-${this.jobStateService.selectedModel()}`;
    if (job.modelGrid.is2d) {
      return `${prefix}-${suffix}-Height${this.viewerSettings.viewerHeight()}${this.screenshotFrame.fileTag()}-${job.id}.png`;
    }
    return (
      `${prefix}-IL${this.jobStateService.inline()}-XL${this.jobStateService.crossline()}` +
      `-DEP${this.jobStateService.depth()}-${suffix}` +
      `-Height${this.viewerSettings.viewerHeight()}_${this.viewerSettings.depthViewerHeight()}` +
      `${this.screenshotFrame.fileTag()}-${job.id}.png`
    );
  }

  // kept on the service for model-new's single-view screenshot buttons
  downloadDataUrl(name: string, href: string) {
    downloadDataUrl(name, href);
  }
}

// local copy (also defined in model-new.component / chart-slice / plots) — importing it
// from model-new.component here would create a circular module dependency
function stripDash(str: string): string {
  if (str.startsWith("-")) {
    return stripDash(str.substring(1));
  } else if (str.endsWith("-")) {
    return stripDash(str.substring(0, str.length - 1));
  }
  return str;
}
